import * as React from 'react';

interface Logo {
  alt: string;
  src: string;
}

interface LogoCloudProps {
  className?: string;
  logos?: Logo[];
}

// New interface for the mini version with grid customization
interface MiniLogoCloudProps {
  className?: string;
  logos?: Logo[];
  maxLogos?: number;
  mobileColumns?: 1 | 2 | 3 | 4;
  desktopColumns?: 1 | 2 | 3 | 4 | 5 | 6;
  gap?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  opacity?: number;
  text?: string;
  textClassName?: string;
}

export const logos = [
  {
    alt: 'Disney',
    src: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/4698b545-58c7-4460-9e1f-a2e3a756c400/public'
  },
  {
    alt: 'LinkedIn',
    src: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/1807e9c5-4fb0-4d11-5e7c-1ed71f78ce00/public'
  },
  {
    alt: 'Meta',
    src: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/861c7d02-f4ae-4f20-02d1-c9e31ef16200/public'
  },
  {
    alt: 'Google',
    src: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/fe82553f-aedf-471b-ac9a-6e07f81d1c00/public'
  },
  {
    alt: 'Microsoft',
    src: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/4da1d5b9-1d3e-4d35-ac23-4777ef58b600/public'
  },
  {
    alt: 'Amazon',
    src: 'https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/c20a60f1-7bdd-426f-7bbb-4c1f45baf000/public'
  }
];

// Default text used in both components for consistency
const DEFAULT_TRUST_TEXT = 'Trusted by employees at companies large and small';

export function LogoCloud({ className = '', logos: customLogos }: LogoCloudProps) {
  const logosToShow = customLogos || logos;

  return (
    <div className={`mx-auto max-w-screen-2xl px-6 lg:px-8 flex flex-col items-center ${className}`}>
      <div className="text-base text-black/50 mx-auto flex text-center w-fit mb-4">{DEFAULT_TRUST_TEXT}</div>
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-4 opacity-40">
        {logosToShow.map((logo, index) => (
          <img
            key={index}
            alt={logo.alt}
            src={logo.src}
            width={158}
            height={48}
            className="max-h-12 w-full object-contain lg:col-span-1"
          />
        ))}
      </div>
    </div>
  );
}

// New mini version of the LogoCloud component
export function MiniLogoCloud({
  className = '',
  logos: customLogos,
  maxLogos,
  mobileColumns = 2,
  desktopColumns = 4,
  gap = 'md',
  opacity = 40,
  text = DEFAULT_TRUST_TEXT,
  textClassName = 'text-base text-black/50 mx-auto flex text-center w-fit mb-4'
}: MiniLogoCloudProps) {
  let logosToShow = customLogos || logos;

  // Limit the number of logos if maxLogos is specified
  if (maxLogos !== undefined && maxLogos > 0) {
    logosToShow = logosToShow.slice(0, maxLogos);
  }

  // Map gap size to Tailwind classes
  const gapSizeMap = {
    none: 'gap-0',
    xs: 'gap-1',
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6',
    xl: 'gap-8'
  };

  // Get the appropriate gap class
  const gapClass = gapSizeMap[gap];

  // Get the appropriate grid column classes
  const mobileGridClass = `grid-cols-${mobileColumns}`;
  const desktopGridClass = `lg:grid-cols-${desktopColumns}`;

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {text && <div className={textClassName}>{text}</div>}
      <div className={`grid ${mobileGridClass} ${desktopGridClass} ${gapClass} opacity-${opacity}`}>
        {logosToShow.map((logo, index) => (
          <img
            key={index}
            alt={logo.alt}
            src={logo.src}
            width={158}
            height={48}
            className="max-h-12 w-full object-contain"
          />
        ))}
      </div>
    </div>
  );
}
